import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Contact from './pages/Contact';
import Industries from './pages/Industries';
import Manufacturing from './pages/Manufacturing';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Admin from './pages/Admin';
import NotFound from './pages/NotFound';
import SideButton from './components/SideButton';
import MorphicBackground from './components/MorphicBackground';
import { loadLanguage } from './services/translationService';
import { translateDOM } from './utils/domTranslator';
import { SiteAssetsProvider } from './context/SiteAssetsContext';
import './App.css';
import './components/BlogStyles.css';
import './styles/brandColors.css';

function App() {
  const [theme, setTheme] = useState('dark');
  const [language, setLanguage] = useState('en');
  const [translating, setTranslating] = useState(false);
  const [langVersion, setLangVersion] = useState(0);

  // Load theme from localStorage on initial render
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  // Apply theme to body class
  useEffect(() => {
    document.body.className = theme === 'dark' ? 'bg-black text-white' : 'bg-white text-gray-900';
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Save language preference
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // DOM-level translation: runs after React renders to catch ALL text including hardcoded strings.
  // Uses multiple delayed passes to catch async-loaded content (blog posts, etc.)
  useEffect(() => {
    if (language === 'en') {
      translateDOM('en');
      return;
    }
    // Immediate pass after paint
    const raf = requestAnimationFrame(() => {
      translateDOM(language);
    });
    // Delayed passes to catch async content (blog posts loading from Firebase/localStorage)
    const t1 = setTimeout(() => translateDOM(language), 600);
    const t2 = setTimeout(() => translateDOM(language), 1800);
    const t3 = setTimeout(() => translateDOM(language), 4000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [language, langVersion]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  const changeLanguage = async (newLanguage) => {
    const STATIC = ['en', 'zh', 'es'];
    // Switch language immediately for instant UI response
    setLanguage(newLanguage);
    
    if (!STATIC.includes(newLanguage)) {
      setTranslating(true);
      try {
        await loadLanguage(newLanguage);
      } catch {}
      setTranslating(false);
      // Trigger re-render so components pick up the newly loaded translations
      setLangVersion(v => v + 1);
    }
  };

  return (
    <HelmetProvider>
      <SiteAssetsProvider>
        <Router>
          <div className={`App flex flex-col min-h-screen transition-colors duration-500 ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-gray-900'}`} style={{ position: 'relative' }}>
            <MorphicBackground theme={theme} />
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Header theme={theme} toggleTheme={toggleTheme} language={language} changeLanguage={changeLanguage} translating={translating} key={`header-${language}-${langVersion}`} />
              <main className="flex-grow" key={`main-${language}-${langVersion}`}>
                <Routes>
                  <Route path="/" element={<Home theme={theme} language={language} />} />
                  <Route path="/about" element={<About theme={theme} language={language} />} />
                  <Route path="/services" element={<Services theme={theme} language={language} />} />
                  <Route path="/industries" element={<Industries theme={theme} language={language} />} />
                  <Route path="/manufacturing" element={<Manufacturing theme={theme} language={language} />} />
                  <Route path="/contact" element={<Contact theme={theme} language={language} />} />
                  <Route path="/blog" element={<Blog theme={theme} language={language} />} />
                  <Route path="/blog/:id" element={<BlogPost theme={theme} language={language} />} />
                  <Route path="/admin" element={<Admin theme={theme} language={language} />} />
                  <Route path="*" element={<NotFound theme={theme} language={language} />} />
                </Routes>
              </main>
              <Footer theme={theme} language={language} key={`footer-${language}-${langVersion}`} />
              <SideButton theme={theme} />
            </div>
          </div>
        </Router>
      </SiteAssetsProvider>
    </HelmetProvider>
  );
}

export default App;